import React, { useState, useEffect, useMemo, useRef, forwardRef } from 'react';
import { View, StyleSheet } from 'react-native';

import PropTypes from 'prop-types';

import { usePrevious } from '../Utils/Hooks';

import LottieView from './Lottie.Coverter.web';
import {
  AnimationFilesGetters,
  ToBiEmojis,
  ToBiIdleTypes,
  TobiTypesJson,
  TobiTypes,
} from './ToBi.Enums';
import styles from './ToBi.Styles';

function getContainerStyle(size, containerStyle) {
  const style = { width: size, height: size, borderRadius: size / 2 };

  return StyleSheet.flatten([styles.containerStyle, style, containerStyle]);
}

function getToBiStyle(size, tobiSize, style) {
  let tobiStyle = { width: size * 2, height: size * 2 };

  if (tobiSize) {
    tobiStyle = { width: tobiSize, height: tobiSize };
  }

  return StyleSheet.flatten([styles.tobiStyle, tobiStyle, style]);
}

function validateEmoji(emoji) {
  /*
    The emojis enum the key is the same as the value which is way we used the bellow approach, incase of adding a new emoji
    if you decide to add a key that is different than the value
    change the first part of the bellow condition from
    `emoji in ToBiEmojis` to `Object.values(ToBiEmojis).indexOf(emoji) !== -1`
    we check using the key because it is much faster to use `key in obj` and this check is done whenever the emoji desired changes
  */
  return emoji in ToBiEmojis;
}

const ToBi = forwardRef(
  (
    {
      emoji,
      onEmojiFinish,
      size,
      tobiSize,
      style,
      containerStyle,
      idleType = ToBiIdleTypes.short,
      tobiType,
      ...props
    },
    ref,
  ) => {
    const lottieRef = useRef(undefined);

    const [currentEmoji, setCurrentEmoji] = useState();

    const previousEmoji = usePrevious(currentEmoji);

    const properContainerStyle = useMemo(
      () => getContainerStyle(size, containerStyle),
      [size, containerStyle],
    );
    const properTobiStyle = useMemo(
      () => getToBiStyle(size, tobiSize, style),
      [size, tobiSize, style],
    );

    useEffect(() => {
      emoji && validateEmoji(emoji);
      if (emoji !== currentEmoji && !previousEmoji) {
        const currentEmo = AnimationFilesGetters[emoji];
        setCurrentEmoji(emoji);
        setTimeout(() => {
          setCurrentEmoji(null);
        }, currentEmo?.duration ?? 2000);
      }
    }, [emoji, currentEmoji, previousEmoji]);

    const currentEmo = AnimationFilesGetters[currentEmoji];
    const IdleEmo = AnimationFilesGetters.IdleOneShort;
    return (
      <View style={properContainerStyle} accessible>
        <LottieView
          enableMergePathsAndroidForKitKatAndAbove={false}
          hardwareAccelerationAndroid
          cacheStrategy="strong"
          {...props}
          ref={lottieRef}
          imageAssetsFolder="lottie/ToBi"
          source={TobiTypesJson[tobiType]()}
          autoPlay
          style={properTobiStyle}
          loop={true}
          currentSegmants={
            currentEmo
              ? [currentEmo.startFrame, currentEmo.endFrame, currentEmo.duration]
              : [IdleEmo.startFrame, IdleEmo.endFrame, IdleEmo.duration]
          }
        />
      </View>
    );
  },
);

ToBi.displayName = 'ToBi';

ToBi.defaultProps = {
  onEmojiFinish: ({ isCanceled, emoji, nextEmoji, repeat }) => { },
  size: 200,
  tobiSize: undefined,
  tobiType: TobiTypes.default,
};

ToBi.propTypes = {
  emoji: PropTypes.oneOfType([
    PropTypes.oneOf(Object.values(ToBiEmojis)),
    PropTypes.arrayOf(PropTypes.oneOf(Object.values(ToBiEmojis))),
  ]),
  onEmojiFinish: PropTypes.func,
  size: PropTypes.number,
  tobiSize: PropTypes.number,
  style: PropTypes.object,
  containerStyle: PropTypes.object,
  idleType: PropTypes.oneOf(Object.values(ToBiIdleTypes)),
  tobiType: PropTypes.oneOf(Object.values(TobiTypes)),
};

export default ToBi;
